import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Loading from "../loading/Loading";
import Pagination from "../pagination/Pagination";
import {
  getTeamByIdService,
  getTeamMembersService,
} from "../../services/teams/services";
import NoResults from "../noResults/noResults";
import InviteUserModal from "../inviteUserModal/InviteUserModal";
import { useTranslator } from "../../context/translator";
import PermissionsConfigurationModal from "components/permissions/permissionsConfigurationModal/PermissionsConfigurationModal";
import Can from "../can/Can";
import { PERMISSION_CODE } from "../../utils/enum/enums";
import { useGetCompanyQuery, useGetUserByIdQuery } from "store/api/apiSlice";

const ITEMS_PER_PAGE = 5;

const TableTeam = ({ teamId }) => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);
  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );
  const { translate } = useTranslator();

  const [employeeData, setEmployeeData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalEmployee, setTotalEmployee] = useState(0);
  const [search, setSearch] = useState("");
  const [team, setTeam] = useState({ _id: "", name: "", companyId: "" });
  const [page, setPage] = useState(1);

  const [permissionModalOpen, SetPermissionModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState({});

  const [reopenUserPermissionsUser, SetReopenUserPermissionsUser] =
    useState(null);

  const ModalPermissionUserShow = (userr) => {
    //console.log("userr", userr);

    setSelectedUser({ ...userr });
    SetPermissionModalOpen(true);
  };
  const HandlePermissionModalClose = (type) => {
    SetPermissionModalOpen(false);

    // Cerrado modal por defaultPermissions no coincide base con front-end
    if (type !== null && type === 70) {
      // en teor�a al setear eso, deber�a refrescar por el useCallback
      SetReopenUserPermissionsUser({ ...selectedUser });
    }

    setSelectedUser({});
  };

  const totalPages = Math.ceil(totalEmployee / ITEMS_PER_PAGE);

  const handleBack = () => navigate(-1);

  const handleNewTeamMember = () => navigate(`/team/${teamId}/new`);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const viewEmployee = (id) => navigate(`/team/${teamId}/${id}`);

  const fetchTeamMembers = useCallback(async () => {
    //console.log("fetchTeamMembers");

    try {
      setIsLoading(true);

      let params = {
        companyId: company.id,
        teamId: teamId,
        page: page,
        pageSize: ITEMS_PER_PAGE,
        search: search,
      };

      getTeamMembersService(params).then((callback) => {
        setEmployeeData(callback?.teams ?? []);
        setTotalEmployee(callback?.totalTeams ?? 0);

        if (reopenUserPermissionsUser !== null) {
          const members = callback?.teams ?? [];

          const member = members.find(
            (a) => a._id === reopenUserPermissionsUser._id,
          );

          //console.log("member", member);

          if (member !== null) {
            ModalPermissionUserShow(member);
          }

          SetReopenUserPermissionsUser(null);
        }
      });
    } finally {
      setIsLoading(false);
    }
  }, [company.id, page, search, teamId, reopenUserPermissionsUser]);

  const fetchTeam = useCallback(async () => {
    try {
      const data = await getTeamByIdService(user, teamId);
      if (data?.team) setTeam(data.team);
    } catch {}
  }, [teamId, user]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  if (userLoading || companyLoading) return null;

  return (
    <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
      <div className="flex items-center mt-2 ml-2 gap-2">
        <button
          type="button"
          onClick={handleBack}
          className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 
                     focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm p-2.5 
                     text-center inline-flex items-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800 rotate-180"
        >
          <svg
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 14 10"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M1 5h12m0 0L9 1m4 4L9 9"
            />
          </svg>
        </button>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
          {team.name}
        </h3>
      </div>

      <div className="grid gap-6 mb-6 md:grid-cols-2 grid-cols-2 mt-2 mx-2">
        <div>
          <label
            htmlFor="search"
            className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
          >
            {translate("search")}
          </label>
          <input
            id="search"
            type="text"
            value={search}
            onChange={handleSearchChange}
            placeholder={`${translate("search")}...`}
            className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg 
                       focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 
                       dark:bg-gray-700 dark:border-gray-600 dark:text-white"
          />
        </div>

        <div className="flex justify-end items-center gap-4">
          <Can permissions={[PERMISSION_CODE.CREATE_USER]}>
            <button
              type="button"
              onClick={handleNewTeamMember}
              className="text-white bg-gradient-to-r from-green-400 via-green-500 to-green-600 
                       hover:bg-gradient-to-br focus:ring-4 focus:outline-none 
                       focus:ring-green-300 dark:focus:ring-green-800 font-medium rounded-lg 
                       text-sm px-5 py-2.5"
            >
              {translate("new-member")}
            </button>
          </Can>
          <Can permissions={[PERMISSION_CODE.INVITE_USER]}>
            <InviteUserModal
              teamId={teamId}
              loadTeamMembers={fetchTeamMembers}
            />
          </Can>
        </div>
      </div>

      {/* Tabla */}
      {isLoading && employeeData.length === 0 ? (
        <Loading />
      ) : employeeData.length === 0 ? (
        <NoResults message="No members found" />
      ) : (
        <>
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th className="px-6 py-3">{translate("name")}</th>
                <th className="px-6 py-3">{translate("role")}</th>
                <th className="px-6 py-3">{translate("status")}</th>
                <th className="px-6 py-3">
                  <span className="sr-only">{translate("edit")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {employeeData.map((emp) => (
                <tr
                  key={emp._id}
                  className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  <th
                    scope="row"
                    className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                  >
                    {emp.firstName} {emp.lastName}
                  </th>
                  <td className="px-6 py-4">{emp?.role?.name || "-"}</td>
                  <td className="px-6 py-4">
                    {emp.status ? "Active" : "Deactivate"}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      type="button"
                      onClick={() => viewEmployee(emp._id)}
                      className="font-medium text-blue-600 dark:text-blue-500 hover:underline"
                    >
                      {translate("view")}
                    </button>

                    {(user?.role.sa === true || user?.role.admin === true) && (
                      <span>
                        &nbsp;&nbsp;
                        <button
                          type="button"
                          onClick={() => ModalPermissionUserShow(emp)}
                          className="font-medium text-blue-600 dark:text-blue-500 hover:underline"
                        >
                          {translate("permissions")}
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {isLoading && <Loading />}

          <Pagination
            key="paginationCustomer"
            data={employeeData}
            page={page}
            pageNumber={totalPages}
            isLoading={isLoading}
            setPage={setPage}
            totalRecords={totalEmployee}
          ></Pagination>

          <PermissionsConfigurationModal
            openP={permissionModalOpen}
            companyId={company.id}
            roleId={selectedUser?.role?._id}
            selectedType="USER"
            selectedItem={selectedUser}
            disabledPermissions={selectedUser.defaultPermissions}
            onClose={HandlePermissionModalClose}
          />
        </>
      )}
    </div>
  );
};

export default TableTeam;
